// sendFlow.js — G1-WS1-T02 (refactor-deepening-r2 / G1, Send Flow / Spec Gate seam).
//
// Deep module owning the Spec Gate phase-1 that ran inline at the top of
// SessionWorkspace.handleSend: generate a spec artifact for THIS turn, push the
// spec_review card, stash the pending turn context, and DEFER the agent run until the
// user Accepts/Skips (resumed by useTurnDispatch). Skip-safe: a null spec / abort /
// timeout / a session switch during generation all fall through cleanly.
//
// Deletion test (01-DOCTRINE): delete runSpecGate and the AbortController+timeout, the
// local/cloud spec chatFn branch, the generateSpec call, the finally own-controller
// guard, the switched-away/aborted guards, the spec_review artifact push, the
// pendingSpecTurn stash, and the skip-safe fall-through signal all scatter back into
// handleSend. It concentrates "does THIS turn become a spec review, and if so wire the
// whole mechanism" behind one call that returns a single boolean. DEEP.
//
// Behavior byte-identical to the inline block: same order (busy → livestatus → arm
// timeout → build chatFn → generateSpec → finally guard → session/abort guards →
// artifact + stash), same SPEC_TIMEOUT_MS, same local (Ollama/LM Studio) vs cloud
// (agent-turn spec_probe) chatFn, same skip-safe fall-through. The pure helpers
// (generateSpec / runSpecCheckIfEnabled / asSessionId) are imported; the turn payload +
// the component state (setters/refs/session/route/steering/apiFetch) are injected.

import { generateSpec } from '../../../lib/specGate.js';
import { runSpecCheckIfEnabled } from '../../../lib/specGateLive.js';
import { asSessionId } from '../../../lib/brandedIds.js';

const SPEC_TIMEOUT_MS = 45000;

/**
 * Build the spec-generation chatFn for THIS route (module-private). Kept out of
 * runSpecGate so the orchestrator stays under the complexity/max-lines threshold.
 *
 * I3 — multi-LLM LAW: use route.model / route.llmAdapterId (NOT the raw effModel
 * selector, which can be `adapter:<id>:<model>` that findModel rejects). The signal
 * threads abort into the generation; cloud sets spec_probe so the server runs the LLM
 * but records NO turn/cost (I4).
 */
function buildSpecChatFn(route, controller, { session, apiFetch, scopedSteering, text }) {
  if (route.kind === 'local') {
    return async ({ messages: msgs }) => {
      const ns = route.isLmStudio ? window.apolo?.lmstudio : window.apolo?.ollama;
      const r = await ns?.chat?.({ model: route.model, messages: msgs, signal: controller.signal });
      return { ok: !!r?.ok, content: r?.content || '' };
    };
  }
  return async ({ messages: msgs }) => {
    const res = await apiFetch(`/api/code-ide/sessions/${session.id}/agent-turn`, {
      method: 'POST',
      body: {
        messages: msgs,
        model: route.model,
        llm_adapter_id: route.llmAdapterId || null,
        tools: [],
        // F6 — thread steering into the probe so the server injects it (prepareTurn
        // sanitizes+injects steering on EVERY turn, probes included).
        steering: scopedSteering(text),
        spec_probe: true,
      },
      signal: controller.signal,
    });
    return { ok: !!res.ok, content: res.data?.content || '' };
  };
}

/**
 * Run the Spec Gate for one send. Returns whether handleSend should stop here.
 *
 * @param {{ text: string, route: any, effModel: string, permissionsMode: string,
 *           attachments: any, routeDecision: any }} payload  the turn being sent
 * @param {{
 *   session: any, sessionIdRef: { current: any }, specGenAbortRef: { current: any },
 *   setGeneratingSpec: (b: boolean) => void, setLiveStatus: (s: any) => void,
 *   setMessages: (fn: (m: any[]) => any[]) => void, setPendingSpecTurn: (p: any) => void,
 *   scopedSteering: (text: string) => any[], apiFetch: (url: string, opts: any) => Promise<any>,
 * }} deps  injected component state
 * @returns {Promise<boolean>} true ⇒ handled: the caller must return (turn stashed
 *   pending Accept, OR the user switched sessions mid-generation). false ⇒ fall through
 *   to runAgent (spec null / aborted / same session — skip-safe).
 */
export async function runSpecGate(
  { text, route, effModel, permissionsMode, attachments, routeDecision },
  {
    session, sessionIdRef, specGenAbortRef,
    setGeneratingSpec, setLiveStatus, setMessages, setPendingSpecTurn,
    scopedSteering, apiFetch,
  },
) {
  const genSessionId = asSessionId(session?.id);
  // I2 — drive a visible busy state + an abort handle so the composer shows Stop, ESC
  // cancels, and a hung server can't strand the user. A timeout aborts and falls
  // through to a normal turn (skip-safe).
  const controller = new AbortController();
  specGenAbortRef.current = controller;
  setGeneratingSpec(true);
  setLiveStatus({ phase: 'pensando' });
  const timeoutId = setTimeout(() => {
    try { controller.abort(); } catch { /* noop */ }
  }, SPEC_TIMEOUT_MS);

  const specChatFn = buildSpecChatFn(route, controller, { session, apiFetch, scopedSteering, text });

  // Read steering sources at call time (no stale closure).
  let spec = null;
  try {
    spec = await generateSpec({
      request: text,
      chatFn: specChatFn,
      model: route.model,
      steering: scopedSteering(text),
    });
  } catch {
    spec = null; // abort/timeout/failure → skip-safe fall-through
  } finally {
    clearTimeout(timeoutId);
    // Only clear the busy state if THIS generation still owns it (a session switch /
    // supersede may have already reset it).
    if (specGenAbortRef.current === controller) {
      specGenAbortRef.current = null;
      setGeneratingSpec(false);
      setLiveStatus(null);
    }
  }

  // C2 — if the user switched sessions during generation, or aborted, do NOT inject the
  // artifact into the (now wrong) session. Fall through quietly.
  const switchedAway = sessionIdRef.current !== genSessionId;
  if (spec && !controller.signal.aborted && !switchedAway) {
    const turnToken = spec.id; // already a unique UUID from generateSpec
    // Push the spec review artifact as a live (ephemeral) tool message. Stamp sessionId
    // in metadata so the artifact/event is session-aware (C2).
    setMessages((m) => [
      ...m,
      {
        id: `spec-${Date.now()}`,
        role: 'tool',
        tool_name: 'spec_review',
        metadata: { spec, turnToken, sessionId: genSessionId, specCheck: runSpecCheckIfEnabled(spec) },
        created_at: new Date().toISOString(),
      },
    ]);
    // Stash everything needed to resume after the user accepts/skips, stamped with the
    // session so the resume listeners can bail on a mismatch (C2).
    setPendingSpecTurn({ // E9-T01 — persiste (sobrevive ao reload)
      sessionId: genSessionId,
      turnToken,
      text,
      route,
      effModel,
      permissionsMode,
      attachments,
      routeDecision,
    });
    // Do NOT run the agent yet — wait for Accept/Skip event.
    return true;
  }
  // spec null / aborted / switched → fall through to normal runAgent (skip-safe), UNLESS
  // we switched away (then the turn belongs to a session we left → caller returns).
  return switchedAway;
}
